import { useEffect, useRef } from "react";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";

export type LoomControlSelectionIntent =
  | { readonly kind: "node"; readonly nodeId: string; readonly requestId: number }
  | { readonly kind: "control"; readonly nodeId: string; readonly controlSurfaceId: string;
      readonly requestId: number };

export function LoomControlSelectionBridge({ intent, onSettled }: {
  readonly intent: LoomControlSelectionIntent | null;
  readonly onSettled: (requestId: number) => void;
}) {
  const controls = useLoomControlSurfaceContext();
  const appliedRequest = useRef<number | null>(null);
  const settledRequest = useRef<number | null>(null);
  const targetActive = Boolean(intent && controls?.selectedWing?.id === intent.nodeId);
  const definitionReady = Boolean(targetActive
    && (!controls?.collection || controls.definitionRevision));
  const controlExists = Boolean(intent?.kind === "control" && controls?.collection
    ?.controlSurfaces.some(({ id }) => id === intent.controlSurfaceId));

  useEffect(() => {
    if (!intent || !controls || !definitionReady || appliedRequest.current === intent.requestId) return;
    if (intent.kind === "control" && !controlExists) return;
    appliedRequest.current = intent.requestId;
    if (intent.kind === "node") controls.selectBody(`${intent.nodeId}:fixed`);
    else controls.selectControlSurface(intent.controlSurfaceId);
  }, [controlExists, controls, definitionReady, intent]);

  useEffect(() => {
    if (!intent || !targetActive || appliedRequest.current !== intent.requestId
        || settledRequest.current === intent.requestId) return;
    const confirmed = intent.kind === "node"
      ? controls?.selectedControl === null
      : controls?.selectedControl?.id === intent.controlSurfaceId;
    if (!confirmed) return;
    settledRequest.current = intent.requestId;
    onSettled(intent.requestId);
  }, [controls?.selectedControl, intent, onSettled, targetActive]);
  return null;
}
